import axios from "axios";
import { getApps, initializeApp } from "firebase/app";
import { browserPopupRedirectResolver, browserSessionPersistence, initializeAuth, OAuthProvider, SAMLAuthProvider, signInWithPopup, signOut } from "firebase/auth";
import { TenantSSOError } from "./errors";
const SSO_APP_NAME = "tenant-sso";
// GCIP requires provider ids to carry their protocol as a prefix, so the id is
// the authority when identity's kind is missing or unrecognised.
const kindOf = (providerId, kind) => {
    if (kind === "saml" || kind === "oidc") {
        return kind;
    }
    if (providerId.startsWith("saml.")) {
        return "saml";
    }
    return providerId.startsWith("oidc.") ? "oidc" : null;
};
const failureForIdentityError = (body) => {
    const error = body?.error;
    if (error?.code === "AUTH_USER_DISABLED") {
        return "DISABLED";
    }
    if (error?.code === "PERMISSION_DENIED") {
        return "NOT_PROVISIONED";
    }
    return "FAILED";
};
const failureForFirebaseError = (e) => {
    switch (e?.code) {
        case "auth/user-disabled":
            return "DISABLED";
        case "auth/popup-blocked":
            return "POPUP_BLOCKED";
        case "auth/popup-closed-by-user":
            return "CANCELLED";
        case "auth/cancelled-popup-request":
            return "SUPERSEDED";
        default:
            return "FAILED";
    }
};
/**
 * Sign-in through an organization's own identity provider (SAML or OIDC on its
 * GCIP tenant).
 */
class TenantSSO {
    constructor(sdk) {
        this.sdk = sdk;
        this.auths = new Map();
    }
    /**
     * Finds the SSO provider for an email's organization. Only the domain is
     * consulted, so a bare domain works too.
     * @returns null when the organization does not sign in through SSO.
     */
    async discover(emailOrDomain) {
        const value = emailOrDomain.trim();
        const email = value.includes("@") ? value : `sso@${value}`;
        let data;
        try {
            ({ data } = await axios.post(`${this.sdk.urls.identityBaseUrl}/auth/sso/discover`, { email }));
        }
        catch {
            throw new TenantSSOError("LOOKUP_FAILED");
        }
        if (!data?.tenantId || !data.providerId) {
            return null;
        }
        const kind = kindOf(data.providerId, data.kind);
        if (!kind) {
            throw new TenantSSOError("LOOKUP_FAILED");
        }
        return { tenantId: data.tenantId, providerId: data.providerId, kind };
    }
    /**
     * Signs in through the organization's identity provider in a popup, redeems
     * the result with identity and starts the user's session. Call it straight
     * from a click: browsers block popups opened without a user gesture.
     * @param options.authDomain the host serving the IdP handler; defaults to the
     * project's firebaseapp.com domain. Pass the app's own host when Firebase
     * Hosting serves it, so the sign-in stays first-party.
     * @param options.verify rejects a user the caller will not admit, as in trySession.
     */
    async signIn(provider, options = {}) {
        const kind = kindOf(provider.providerId, provider.kind);
        if (!kind) {
            throw new TenantSSOError("FAILED");
        }
        let refreshToken;
        try {
            const auth = this.tenantAuth(options.authDomain || `${this.sdk.madFire.projectId}.firebaseapp.com`);
            auth.tenantId = provider.tenantId;
            const authProvider = kind === "oidc"
                ? new OAuthProvider(provider.providerId)
                : new SAMLAuthProvider(provider.providerId);
            const result = await signInWithPopup(auth, authProvider, browserPopupRedirectResolver);
            refreshToken = result.user.refreshToken;
            // Identity mints the session from here on; this user is not needed again.
            await signOut(auth).catch(() => undefined);
        }
        catch (e) {
            throw new TenantSSOError(failureForFirebaseError(e));
        }
        let account;
        try {
            await this.sdk.authentication.settleLogout();
            ({ data: account } = await axios.post(`${this.sdk.urls.identityBaseUrl}/auth/login/tenant-sso`, { refresh_token: refreshToken }, 
            // identity is on api.*, another origin: without credentials the browser
            // drops the refresh cookie that /auth/refresh later needs.
            { withCredentials: true }));
        }
        catch (e) {
            throw new TenantSSOError(failureForIdentityError(axios.isAxiosError(e) ? e.response?.data : undefined));
        }
        // The exchange already set identity's session cookies, so a refusal from
        // here on must sign out too, as trySession does, or they would outlive it.
        // The IdP owns the second factor; identity has no prompt to offer here.
        try {
            const { verify } = options;
            if (account.mfaRequired || !account.user || (verify && !verify(account.user))) {
                throw new Error("refused");
            }
            return await this.sdk.authentication.initializeUserSession(account);
        }
        catch {
            await this.sdk.authentication.signOut();
            throw new TenantSSOError("FAILED");
        }
    }
    // A separate app per auth domain: authDomain is fixed when an app is created,
    // and the shared app keeps the project default for every other sign-in.
    tenantAuth(authDomain) {
        let auth = this.auths.get(authDomain);
        if (!auth) {
            const name = `${SSO_APP_NAME}:${authDomain}`;
            const app = getApps().find((existing) => existing.name === name) ??
                initializeApp({ ...this.sdk.madFire.firebase.options, authDomain }, name);
            auth = initializeAuth(app, {
                persistence: browserSessionPersistence,
                popupRedirectResolver: browserPopupRedirectResolver
            });
            this.auths.set(authDomain, auth);
        }
        return auth;
    }
}
export default TenantSSO;
//# sourceMappingURL=tenantSSO.js.map